import { lazy, Suspense } from 'react';
import { Skeleton } from '@monksflow/monks-ui';
import { AudienceCreationState } from '../AudienceCreationState';
import type { AudienceDetailBodyProps } from './AudienceDetailBody.types';

const PersonaDetailV2 = lazy(() =>
  import('@/features/audience-flow-widget').then((m) => ({ default: m.PersonaDetailV2 })),
);

/** Enough to fill the widest demographics row, so the wait never looks short. */
const DEMOGRAPHICS_COUNT = 4;
const TRAIT_COUNT = 6;

/**
 * Traces the profile's own blocks - hero, biography, demographics, traits - so
 * the wait has the shape of the answer and nothing jumps when it lands.
 */
function PersonaDetailSkeleton() {
  return (
    <div data-testid="audience-detail-skeleton" className="flex flex-col">
      <div className="flex flex-col gap-md bg-muted p-section">
        <Skeleton className="h-12 w-2/5 rounded-md" />
        <div className="grid grid-cols-2 gap-md">
          <Skeleton className="h-72 rounded-md" />
          <Skeleton className="h-32 rounded-md" />
        </div>
        <Skeleton className="h-12 w-1/2 self-end rounded-md" />
      </div>

      <div className="flex flex-col gap-md p-section">
        <Skeleton className="h-6 w-32 rounded-md" />
        <Skeleton className="h-20 w-full rounded-md" />
      </div>

      <div className="flex flex-col gap-md p-section">
        <Skeleton className="h-6 w-40 rounded-md" />
        <div className="grid grid-cols-4 gap-md">
          {Array.from({ length: DEMOGRAPHICS_COUNT }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-md" />
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-md p-section">
        <Skeleton className="h-6 w-36 rounded-md" />
        <div className="flex flex-wrap gap-xs">
          {Array.from({ length: TRAIT_COUNT }).map((_, index) => (
            <Skeleton key={index} className="h-8 w-24 rounded-md" />
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Whichever of the audience-detail states the fetch settled on, drawn inside
 * the page's single content card.
 */
export function AudienceDetailBody({
  persona,
  isPending,
  onDeleteAndRetry,
  isDeleting,
}: AudienceDetailBodyProps) {
  if (isPending) return <PersonaDetailSkeleton />;
  if (!persona) return null;

  // Nothing is settled to profile until generation finishes.
  if (persona.creationStatus !== 'ready') {
    return (
      <AudienceCreationState
        persona={persona}
        onDelete={onDeleteAndRetry}
        isDeleting={isDeleting}
      />
    );
  }

  return (
    <Suspense fallback={null}>
      <PersonaDetailV2 persona={persona} />
    </Suspense>
  );
}

AudienceDetailBody.displayName = 'AudienceDetailBody';
